import React, { useState, useMemo } from "react";
import { IoClose } from "react-icons/io5";
import toast from "react-hot-toast";
import API from "../api";
import { capture } from "../instrument";
import { STATUS_CONFIG } from "../utils";

const MergeAdmissionsModal = ({ selectedAdmissions, admissions, setShowMergeModal, setSelectedAdmissions, refreshAdmissions }) => {
  const [primaryAdmissionId, setPrimaryAdmissionId] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedAdmissionsList = useMemo(() => {
    return admissions.filter((admission) => selectedAdmissions.has(admission._id));
  }, [selectedAdmissions, admissions]);

  const mergeAdmissions = async () => {
    if (!primaryAdmissionId) return;

    if (window.confirm("Voulez-vous vraiment fusionner ces admissions ? L'opération est irréversible.") === false) return;

    setLoading(true);
    try {
      const secondaryAdmissions = selectedAdmissionsList.filter((a) => a._id !== primaryAdmissionId);

      const response = await API.post(`/admin/admission/${primaryAdmissionId}/merge`, {
        secondaryIds: secondaryAdmissions.map((a) => a._id),
      });

      if (!response.ok) throw new Error(response.message || "Erreur lors de la fusion");

      await refreshAdmissions(primaryAdmissionId);
      toast.success(`${secondaryAdmissions.length} admission(s) fusionnée(s) avec succès`);
      setSelectedAdmissions(new Set());
      setShowMergeModal(false);
    } catch (error) {
      capture(error);
      toast.error(error.message || "Erreur lors de la fusion des admissions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-medium">Fusionner les admissions</h2>
          <button onClick={() => setShowMergeModal(false)} className="text-gray-400 hover:text-gray-600">
            <IoClose className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <h3 className="font-medium mb-2">Sélectionnez l'admission principale</h3>
          <p className="text-sm text-gray-600 mb-4">Les autres admissions seront fusionnées dans celle-ci et archivées.</p>

          <div className="space-y-3">
            {selectedAdmissionsList.map((admission) => (
              <div
                key={admission._id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  primaryAdmissionId === admission._id ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setPrimaryAdmissionId(admission._id)}
              >
                <div className="flex items-start gap-3">
                  <input type="radio" checked={primaryAdmissionId === admission._id} onChange={() => setPrimaryAdmissionId(admission._id)} className="mt-1 text-primary-500" />
                  <div className="flex-1">
                    <div className="font-medium">
                      {admission.patient.firstName} {admission.patient.lastName}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Numéro:</span> {admission.number}
                      {admission.silReservationNumber && <span className="ml-1.5 text-gray-400">· {admission.silReservationNumber}</span>}
                      {admission.silAdmissionNumber && <span className="ml-1.5 text-gray-400">· {admission.silAdmissionNumber}</span>}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Date:</span> {new Date(admission.createdAt).toLocaleDateString("fr-FR")}
                    </div>
                    {admission.status && (
                      <span className="status-pill mt-2" data-status={admission.status}>
                        {STATUS_CONFIG[admission.status]?.label || admission.status}
                      </span>
                    )}
                    <div className="text-sm text-gray-600 mt-2">
                      <span className="font-medium">{admission.prescriptions?.length || 0}</span> ordonnance(s)
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" onClick={() => setShowMergeModal(false)} disabled={loading}>
            Fermer
          </button>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={!primaryAdmissionId || loading}
            onClick={mergeAdmissions}
          >
            {loading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
            Confirmer la fusion
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeAdmissionsModal;
